// AudienceView - Main view for Audience Knowledge System
// Manages Products, Audiences, Playbooks, and Training with tab navigation

import { audienceApi, Product, Audience, Playbook } from '../services/audience-api';
import { TrainingPanel } from './TrainingPanel';

export class AudienceView {
  private containerEl: HTMLElement;
  private currentTab: 'products' | 'audiences' | 'playbooks' | 'training' = 'products';
  private trainingPanel: TrainingPanel | null = null;

  // Data
  private products: Product[] = [];
  private audiences: Audience[] = [];
  private playbooks: Playbook[] = [];
  private selectedProductId: string | null = null;
  private selectedAudienceId: string | null = null;

  constructor() {
    const container = document.getElementById('audience-view');
    if (!container) {
      throw new Error('Audience view container not found');
    }
    this.containerEl = container;
  }

  async load(): Promise<void> {
    await this.loadProducts();
    this.render();
  }

  private async loadProducts(): Promise<void> {
    try {
      this.products = await audienceApi.listProducts();
    } catch (error) {
      console.error('Failed to load products:', error);
      this.products = [];
    }
  }

  private async loadAudiences(productId: string): Promise<void> {
    try {
      this.audiences = await audienceApi.listAudiences(productId);
    } catch (error) {
      console.error('Failed to load audiences:', error);
      this.audiences = [];
    }
  }

  private async loadPlaybooks(productId?: string, audienceId?: string): Promise<void> {
    try {
      this.playbooks = await audienceApi.listPlaybooks({ productId, audienceId });
    } catch (error) {
      console.error('Failed to load playbooks:', error);
      this.playbooks = [];
    }
  }

  private render(): void {
    this.containerEl.innerHTML = `
      <div class="audience-container">
        <div class="audience-header">
          <h2>Audience Knowledge</h2>
          <div class="audience-tabs">
            <button class="tab-btn ${this.currentTab === 'products' ? 'active' : ''}" data-tab="products">
              Products
            </button>
            <button class="tab-btn ${this.currentTab === 'audiences' ? 'active' : ''}" data-tab="audiences">
              Audiences
            </button>
            <button class="tab-btn ${this.currentTab === 'playbooks' ? 'active' : ''}" data-tab="playbooks">
              Playbooks
            </button>
            <button class="tab-btn ${this.currentTab === 'training' ? 'active' : ''}" data-tab="training">
              <span class="material-symbols-outlined">school</span>
              Training
            </button>
          </div>
        </div>
        <div class="audience-content">
          ${this.renderCurrentTab()}
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private renderCurrentTab(): string {
    switch (this.currentTab) {
      case 'products':
        return this.renderProductsTab();
      case 'audiences':
        return this.renderAudiencesTab();
      case 'playbooks':
        return this.renderPlaybooksTab();
      case 'training':
        return '<div id="training-panel-container"></div>';
      default:
        return '<p>Tab not found</p>';
    }
  }

  private renderProductsTab(): string {
    return `
      <div class="tab-panel">
        <div class="panel-header">
          <h3>Products</h3>
          <button class="btn-primary" id="btn-create-product">
            <span class="material-symbols-outlined">add</span>
            New Product
          </button>
        </div>
        <div class="products-list">
          ${this.products.length === 0 ? `
            <div class="empty-state">
              <span class="material-symbols-outlined">inventory_2</span>
              <p>No products yet. Create your first product to get started.</p>
            </div>
          ` : this.products.map(p => `
            <div class="product-card" data-id="${p.id}">
              <div class="product-header">
                <h4>${p.name}</h4>
                <div class="product-actions">
                  <button class="icon-btn" data-action="edit-product" data-id="${p.id}" title="Edit">
                    <span class="material-symbols-outlined">edit</span>
                  </button>
                  <button class="icon-btn" data-action="delete-product" data-id="${p.id}" title="Delete">
                    <span class="material-symbols-outlined">delete</span>
                  </button>
                </div>
              </div>
              <p class="product-description">${p.description}</p>
              <div class="product-meta">
                <span><strong>Value Prop:</strong> ${p.valueProposition}</span>
                <span><strong>Target:</strong> ${p.targetMarket}</span>
              </div>
              <button class="btn-secondary btn-sm" data-action="view-audiences" data-id="${p.id}">
                View Audiences
              </button>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderAudiencesTab(): string {
    return `
      <div class="tab-panel">
        <div class="panel-header">
          <h3>Audiences</h3>
          <div class="panel-actions">
            <select id="product-filter" class="select-input">
              <option value="">All Products</option>
              ${this.products.map(p => `
                <option value="${p.id}" ${p.id === this.selectedProductId ? 'selected' : ''}>
                  ${p.name}
                </option>
              `).join('')}
            </select>
            <button class="btn-primary" id="btn-create-audience" ${!this.selectedProductId ? 'disabled' : ''}>
              <span class="material-symbols-outlined">add</span>
              New Audience
            </button>
          </div>
        </div>
        <div class="audiences-list">
          ${this.audiences.length === 0 ? `
            <div class="empty-state">
              <span class="material-symbols-outlined">group</span>
              <p>${this.selectedProductId ? 'No audiences for this product yet.' : 'Select a product to view audiences.'}</p>
            </div>
          ` : this.audiences.map(a => `
            <div class="audience-card" data-id="${a.id}">
              <div class="audience-header">
                <h4>${a.name}</h4>
                <div class="audience-badges">
                  <span class="badge">${a.awarenessLevel}</span>
                  <span class="badge">${a.funnelStage.toUpperCase()}</span>
                </div>
              </div>
              <p class="audience-description">${a.description}</p>
              <div class="audience-details">
                <div class="detail-section">
                  <strong>Pains:</strong>
                  <ul>${a.pains.slice(0, 3).map(p => `<li>${p}</li>`).join('')}</ul>
                </div>
                <div class="detail-section">
                  <strong>Objectives:</strong>
                  <ul>${a.objectives.slice(0, 3).map(o => `<li>${o}</li>`).join('')}</ul>
                </div>
              </div>
              <div class="audience-actions">
                <button class="btn-secondary btn-sm" data-action="view-playbooks" data-id="${a.id}">
                  View Playbooks
                </button>
                <button class="icon-btn" data-action="edit-audience" data-id="${a.id}">
                  <span class="material-symbols-outlined">edit</span>
                </button>
                <button class="icon-btn" data-action="delete-audience" data-id="${a.id}">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderPlaybooksTab(): string {
    return `
      <div class="tab-panel">
        <div class="panel-header">
          <h3>Playbooks</h3>
          <div class="panel-actions">
            <select id="playbook-product-filter" class="select-input">
              <option value="">All Products</option>
              ${this.products.map(p => `
                <option value="${p.id}" ${p.id === this.selectedProductId ? 'selected' : ''}>
                  ${p.name}
                </option>
              `).join('')}
            </select>
            <button class="btn-primary" id="btn-generate-playbook" ${!this.selectedProductId ? 'disabled' : ''}>
              <span class="material-symbols-outlined">auto_awesome</span>
              Generate Playbook
            </button>
          </div>
        </div>
        <div class="playbooks-list">
          ${this.playbooks.length === 0 ? `
            <div class="empty-state">
              <span class="material-symbols-outlined">menu_book</span>
              <p>${this.selectedProductId ? 'No playbooks yet. Generate your first playbook with AI.' : 'Select a product to view playbooks.'}</p>
            </div>
          ` : this.playbooks.map(pb => `
            <div class="playbook-card" data-id="${pb.id}">
              <div class="playbook-header">
                <h4>${pb.name}</h4>
                <div class="playbook-badges">
                  <span class="badge badge-${pb.generatedBy}">${pb.generatedBy}</span>
                  ${pb.userValidated ? '<span class="badge badge-validated">validated</span>' : ''}
                </div>
              </div>
              <p class="playbook-description">${pb.description}</p>
              <div class="playbook-message">
                <strong>Central Message:</strong>
                <p>${pb.centralMessage}</p>
              </div>
              <div class="playbook-meta">
                <div><strong>Angles:</strong> ${pb.angles.length}</div>
                <div><strong>Outputs:</strong> ${pb.enabledOutputs.join(', ')}</div>
                ${pb.lastEvolved ? `<div><strong>Last Evolved:</strong> ${new Date(pb.lastEvolved).toLocaleDateString()}</div>` : ''}
              </div>
              <div class="playbook-actions">
                <button class="btn-primary btn-sm" data-action="generate-content" data-id="${pb.id}">
                  <span class="material-symbols-outlined">auto_awesome</span>
                  Generate Content
                </button>
                <button class="btn-secondary btn-sm" data-action="view-playbook" data-id="${pb.id}">
                  View Details
                </button>
                <button class="btn-secondary btn-sm" data-action="evolve-playbook" data-id="${pb.id}">
                  <span class="material-symbols-outlined">autorenew</span>
                  Evolve
                </button>
                <button class="icon-btn" data-action="delete-playbook" data-id="${pb.id}">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private attachEventListeners(): void {
    // Tab switching
    this.containerEl.querySelectorAll('[data-tab]').forEach(btn => {
      btn.addEventListener('click', () => {
        const tab = (btn as HTMLElement).dataset.tab as 'products' | 'audiences' | 'playbooks' | 'training';
        this.switchTab(tab);
      });
    });

    // Product actions
    const createProductBtn = this.containerEl.querySelector('#btn-create-product');
    if (createProductBtn) {
      createProductBtn.addEventListener('click', () => this.showCreateProductModal());
    }

    this.containerEl.querySelectorAll('[data-action="view-audiences"]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const productId = (btn as HTMLElement).dataset.id!;
        this.selectedProductId = productId;
        await this.loadAudiences(productId);
        this.switchTab('audiences');
      });
    });

    // Audience actions
    const productFilter = this.containerEl.querySelector('#product-filter') as HTMLSelectElement;
    if (productFilter) {
      productFilter.addEventListener('change', async () => {
        const productId = productFilter.value;
        this.selectedProductId = productId || null;
        if (productId) {
          await this.loadAudiences(productId);
        } else {
          this.audiences = [];
        }
        this.render();
      });
    }

    const createAudienceBtn = this.containerEl.querySelector('#btn-create-audience');
    if (createAudienceBtn) {
      createAudienceBtn.addEventListener('click', () => this.showCreateAudienceModal());
    }

    this.containerEl.querySelectorAll('[data-action="view-playbooks"]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const audienceId = (btn as HTMLElement).dataset.id!;
        this.selectedAudienceId = audienceId;
        await this.loadPlaybooks(this.selectedProductId!, audienceId);
        this.switchTab('playbooks');
      });
    });

    // Playbook actions
    const playbookProductFilter = this.containerEl.querySelector('#playbook-product-filter') as HTMLSelectElement;
    if (playbookProductFilter) {
      playbookProductFilter.addEventListener('change', async () => {
        const productId = playbookProductFilter.value;
        this.selectedProductId = productId || null;
        if (productId) {
          await this.loadPlaybooks(productId);
        } else {
          this.playbooks = [];
        }
        this.render();
      });
    }

    const generatePlaybookBtn = this.containerEl.querySelector('#btn-generate-playbook');
    if (generatePlaybookBtn) {
      generatePlaybookBtn.addEventListener('click', () => this.showGeneratePlaybookModal());
    }

    // Content generation actions
    this.containerEl.querySelectorAll('[data-action="generate-content"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const playbookId = (btn as HTMLElement).dataset.id!;
        this.showGenerateContentModal(playbookId);
      });
    });
  }

  private async switchTab(tab: 'products' | 'audiences' | 'playbooks' | 'training'): Promise<void> {
    this.currentTab = tab;

    // Load data if needed
    if (tab === 'audiences' && this.selectedProductId) {
      await this.loadAudiences(this.selectedProductId);
    } else if (tab === 'playbooks' && this.selectedProductId) {
      await this.loadPlaybooks(this.selectedProductId);
    }

    this.render();

    // Load Training Panel if training tab
    if (tab === 'training') {
      const container = document.getElementById('training-panel-container');
      if (container) {
        if (!this.trainingPanel) {
          this.trainingPanel = new TrainingPanel(container);
        }
        await this.trainingPanel.load();
      }
    }
  }

  private showCreateProductModal(): void {
    // TODO: Implement modal for creating products
    const name = prompt('Product name:');
    if (!name) return;

    const description = prompt('Description:');
    if (!description) return;

    const valueProposition = prompt('Value proposition:');
    if (!valueProposition) return;

    const targetMarket = prompt('Target market:');
    if (!targetMarket) return;

    audienceApi.createProduct({ name, description, valueProposition, targetMarket })
      .then(() => this.loadProducts().then(() => this.render()))
      .catch(err => alert(`Error: ${err.message}`));
  }

  private showCreateAudienceModal(): void {
    // TODO: Implement proper modal
    alert('Create Audience modal - To be implemented');
  }

  private showGeneratePlaybookModal(): void {
    // TODO: Implement proper modal with options
    if (!this.selectedProductId) return;

    if (confirm('Generate a playbook for this product?')) {
      audienceApi.generatePlaybook({ productId: this.selectedProductId })
        .then(() => {
          alert('Playbook generated!');
          return this.loadPlaybooks(this.selectedProductId!);
        })
        .then(() => this.render())
        .catch(err => alert(`Error: ${err.message}`));
    }
  }

  private async showGenerateContentModal(playbookId: string): Promise<void> {
    const playbook = this.playbooks.find(p => p.id === playbookId);
    if (!playbook) return;

    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>Generate Content - ${playbook.name}</h3>
          <button class="icon-btn modal-close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="generate-config">
            <div class="form-group">
              <label>Output Type</label>
              <select id="gen-output-type" class="select-input">
                ${playbook.enabledOutputs.map(type => `
                  <option value="${type}">${type}</option>
                `).join('')}
              </select>
            </div>
            <div class="form-group">
              <label>Provider</label>
              <select id="gen-provider" class="select-input">
                <option value="both">Both (Claude + GetPoppy)</option>
                <option value="claude">Claude Only</option>
                <option value="poppy">GetPoppy Only</option>
              </select>
            </div>
            <div class="form-group">
              <label>Additional Guidance (optional)</label>
              <textarea id="gen-guidance" class="textarea-input" rows="3" placeholder="Any specific instructions or context..."></textarea>
            </div>
            <button id="btn-generate" class="btn-primary">
              <span class="material-symbols-outlined">auto_awesome</span>
              Generate Content
            </button>
          </div>
          <div id="gen-results" class="generate-results hidden">
            <div class="results-grid">
              <div id="claude-result" class="result-panel hidden">
                <h4>Claude</h4>
                <div class="result-meta"></div>
                <div class="result-content"></div>
                <div class="result-actions">
                  <button class="btn-secondary btn-sm" data-action="copy-claude">Copy</button>
                  <button class="btn-secondary btn-sm" data-action="save-claude">Save as Draft</button>
                </div>
              </div>
              <div id="poppy-result" class="result-panel hidden">
                <h4>GetPoppy</h4>
                <div class="result-meta"></div>
                <div class="result-content"></div>
                <div class="result-actions">
                  <button class="btn-secondary btn-sm" data-action="copy-poppy">Copy</button>
                  <button class="btn-secondary btn-sm" data-action="save-poppy">Save as Draft</button>
                </div>
              </div>
            </div>
          </div>
          <div id="gen-loading" class="generate-loading hidden">
            <div class="loading-spinner"></div>
            <p>Generating content...</p>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Event listeners
    const closeBtn = overlay.querySelector('.modal-close');
    closeBtn?.addEventListener('click', () => overlay.remove());

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });

    const generateBtn = overlay.querySelector('#btn-generate') as HTMLButtonElement;
    generateBtn?.addEventListener('click', async () => {
      const outputType = (overlay.querySelector('#gen-output-type') as HTMLSelectElement).value;
      const provider = (overlay.querySelector('#gen-provider') as HTMLSelectElement).value as 'claude' | 'poppy' | 'both';
      const userGuidance = (overlay.querySelector('#gen-guidance') as HTMLTextAreaElement).value;

      // Show loading
      const configDiv = overlay.querySelector('.generate-config') as HTMLElement;
      const loadingDiv = overlay.querySelector('#gen-loading') as HTMLElement;
      const resultsDiv = overlay.querySelector('#gen-results') as HTMLElement;

      configDiv.classList.add('hidden');
      loadingDiv.classList.remove('hidden');
      resultsDiv.classList.add('hidden');

      try {
        const result = await audienceApi.generateContent({
          playbookId,
          outputType: outputType as any,
          provider,
          userGuidance: userGuidance || undefined
        });

        // Hide loading, show results
        loadingDiv.classList.add('hidden');
        resultsDiv.classList.remove('hidden');

        // Display Claude result
        if (result.claude) {
          const claudePanel = overlay.querySelector('#claude-result') as HTMLElement;
          claudePanel.classList.remove('hidden');

          const meta = claudePanel.querySelector('.result-meta') as HTMLElement;
          meta.textContent = `Model: ${result.claude.metadata.model} | Duration: ${result.claude.metadata.duration}ms`;

          const content = claudePanel.querySelector('.result-content') as HTMLElement;
          content.textContent = result.claude.content;
          content.style.whiteSpace = 'pre-wrap';
          content.style.fontFamily = 'var(--font-mono)';
          content.style.fontSize = '13px';

          // Copy button
          const copyBtn = claudePanel.querySelector('[data-action="copy-claude"]');
          copyBtn?.addEventListener('click', () => {
            navigator.clipboard.writeText(result.claude.content);
            alert('Copied to clipboard!');
          });
        }

        // Display GetPoppy result
        if (result.poppy) {
          const poppyPanel = overlay.querySelector('#poppy-result') as HTMLElement;
          poppyPanel.classList.remove('hidden');

          const meta = poppyPanel.querySelector('.result-meta') as HTMLElement;
          meta.textContent = `Template: ${result.poppy.metadata.templateUsed || 'N/A'}`;

          const content = poppyPanel.querySelector('.result-content') as HTMLElement;
          content.textContent = result.poppy.content;
          content.style.whiteSpace = 'pre-wrap';
          content.style.fontFamily = 'var(--font-mono)';
          content.style.fontSize = '13px';

          // Copy button
          const copyBtn = poppyPanel.querySelector('[data-action="copy-poppy"]');
          copyBtn?.addEventListener('click', () => {
            navigator.clipboard.writeText(result.poppy.content);
            alert('Copied to clipboard!');
          });
        }
      } catch (error: any) {
        loadingDiv.classList.add('hidden');
        configDiv.classList.remove('hidden');
        alert(`Error: ${error.message}`);
      }
    });
  }
}
